import { Controller } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import { Link } from "@tiptap/extension-link"
import { TaskList } from "@tiptap/extension-task-list"
import { TaskItem } from "@tiptap/extension-task-item"
import { SlashMenuExtension } from './extensions/slash_menu_extension'
import { BubbleMenuExtension, createBubbleMenuElement } from './extensions/bubble_menu_extension'
import { BlockMenuExtension } from './extensions/block_menu_extension'
import { serializeToMarkdown } from './extensions/markdown_serializer'
import { parseMarkdownToHTML } from './extensions/markdown_parser'

// Notion-like 編輯器 Controller
// 純 Markdown 模式
export default class extends Controller {
  static values = {
    height: { type: String, default: '' }     // 預設為空，會自動使用 textarea 高度
  }

  connect() {
    // 1. 讀取 textarea 初始內容
    this.textarea = this.element.querySelector('textarea')
    if (!this.textarea) {
      console.error('[Notion Editor] 找不到 textarea 元素')
      return
    }

    const initialContent = this.textarea.value

    // 2. 判斷編輯器高度（優先使用 data-notion-height-value，否則使用 textarea 高度）
    this.editorHeight = this.determineEditorHeight()

    console.log('[Notion Editor] 使用 Markdown 模式')

    // 3. 初始化編輯器
    this.initEditor(initialContent)

    // 4. 隱藏原本的 textarea
    this.textarea.style.display = 'none'

    // 5. 監聽 form submit 事件
    this.setupFormSubmit()
  }

  // 決定編輯器高度
  determineEditorHeight() {
    // 如果有明確設定 height value，使用設定值
    if (this.heightValue && this.heightValue.trim() !== '') {
      return this.heightValue
    }

    // 否則使用 textarea 的計算高度
    const computedStyle = window.getComputedStyle(this.textarea)
    const textareaHeight = this.textarea.offsetHeight

    // 如果 textarea 有設定高度，使用它
    if (textareaHeight > 0) {
      return `${textareaHeight}px`
    }

    // 如果 textarea 的 rows 屬性有設定，計算高度
    const rows = this.textarea.getAttribute('rows')
    if (rows) {
      const lineHeight = parseFloat(computedStyle.lineHeight) || 20
      return `${rows * lineHeight}px`
    }

    // 最後的預設值
    return '620px'
  }

  disconnect() {
    // 清理編輯器實例
    if (this.editor) {
      this.editor.destroy()
    }

    // 移除 Bubble Menu 元素
    if (this.bubbleMenuElement) {
      this.bubbleMenuElement.remove()
    }

    // 移除 form submit 監聽
    if (this.form && this.submitHandler) {
      this.form.removeEventListener('submit', this.submitHandler)
    }
  }


  // STEP4/STEP5: 初始化編輯器
  initEditor(content) {
    // 1. 建立編輯器容器
    this.editorWrapper = document.createElement('div')
    this.editorWrapper.className = 'notion-editor-wrapper'
    this.textarea.parentNode.insertBefore(this.editorWrapper, this.textarea)

    // 2. 建立 Bubble Menu 元素
    this.bubbleMenuElement = createBubbleMenuElement(null, 'markdown')
    document.body.appendChild(this.bubbleMenuElement)

    // 3. 根據模式選擇 extensions
    const extensions = this.getExtensions()

    // 4. 解析初始內容
    const parsedContent = this.parseContent(content)

    // 5. 初始化 Tiptap 編輯器
    this.editor = new Editor({
      element: this.editorWrapper,
      extensions: extensions,
      content: parsedContent,
      editorProps: {
        attributes: {
          class: 'notion-editor-content',
          // 使用 min-height 確保最小高度，內容多時自動擴展
          style: `min-height: ${this.editorHeight};`
        }
      }
      // 注意：不使用 onUpdate 即時同步，而是在 form submit 時轉換
    })

    // 6. 更新 Bubble Menu 的 editor 引用並綁定事件
    this.bubbleMenuElement.editor = this.editor
    this.bubbleMenuElement.bindEvents(this.editor)

    // 7. 將 editor 保存到 ProseMirror view 中，讓 extensions 可以訪問
    if (this.editor.view) {
      this.editor.view.state.editor = this.editor
    }
  }

  // 取得 extensions（純 Markdown 模式）
  getExtensions() {
    return [
      StarterKit,
      Link.configure({ openOnClick: false }),
      TaskList,
      TaskItem.configure({ nested: true }),
      SlashMenuExtension.configure({ mode: 'markdown' }),
      BubbleMenuExtension.configure({
        mode: 'markdown',
        element: this.bubbleMenuElement,
      }),
      BlockMenuExtension,
    ]
  }

  // 解析初始內容（Markdown → HTML）
  parseContent(content) {
    if (!content || content.trim() === '') {
      return '<p></p>'
    }
    // 將 Markdown 轉換為 HTML，讓 Tiptap 可以正確顯示格式
    return parseMarkdownToHTML(content)
  }


  // 監聽 form submit
  setupFormSubmit() {
    this.form = this.textarea.closest('form')
    if (!this.form) {
      console.warn('[Notion Editor] 找不到 form 元素')
      return
    }

    // 儲存 handler 以便之後移除
    this.submitHandler = (e) => {
      this.syncToTextarea()
    }

    this.form.addEventListener('submit', this.submitHandler)
  }

  // 在 form submit 時同步內容到 textarea
  syncToTextarea() {
    if (!this.editor) return

    // 使用自定義 serializer 取得 Markdown 格式的內容（不會轉義斜線等字符）
    const json = this.editor.getJSON()
    console.log('[Notion Editor] JSON:', JSON.stringify(json, null, 2))
    const markdown = serializeToMarkdown(json)
    console.log('[Notion Editor] Markdown:', markdown)
    this.textarea.value = markdown
  }
}
